import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { api, downloadBlob, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { PurchasesSummaryCards } from "@/pages/admin/purchases/PurchasesSummaryCards";
import { PurchasesFiltersBar } from "@/pages/admin/purchases/PurchasesFiltersBar";
import { PurchasesTable } from "@/pages/admin/purchases/PurchasesTable";
import { NewPurchaseDialog } from "@/pages/admin/purchases/NewPurchaseDialog";
import { PurchaseDetailDialog } from "@/pages/admin/purchases/PurchaseDetailDialog";
import { RecordPurchasePaymentDialog } from "@/pages/admin/purchases/RecordPurchasePaymentDialog";
import { PurchaseReturnDialog } from "@/pages/admin/purchases/PurchaseReturnDialog";
import { CancelPurchaseDialog, DeletePurchaseDialog } from "@/pages/admin/purchases/CancelDeletePurchaseDialogs";
import { CompanyLedgerDialog } from "@/pages/admin/purchases/CompanyLedgerDialog";
import { emptyForm, emptyItem, resolveWeights, round3, itemPreviewTotal } from "@/pages/admin/purchases/purchaseHelpers";
import { hasAdminSession } from "@/lib/session";

export default function Purchases() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({ supplier_id: "all", purchase_type: "all", payment_status: "all", date_from: "", date_to: "", search: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", mode: "bank_transfer", reference: "", note: "", gold_weight_g: "", gold_purity: "22K", gold_purity_fine_pct: "", rate_per_gram_24k: "" });
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSel, setReturnSel] = useState({});
  const [pieceSel, setPieceSel] = useState({});
  const [returnReason, setReturnReason] = useState("");
  const [editing, setEditing] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [staff, setStaff] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [companyLedger, setCompanyLedger] = useState({ accounts: [], entries: [] });
  const createFileInputRef = useRef(null);
  const detailFileInputRef = useRef(null);

  const openCompanyLedger = async () => {
    setLedgerOpen(true);
    try { const { data } = await api.get("/purchase/company-ledger"); setCompanyLedger(data); }
    catch { toast.error("Could not load Company Ledger"); }
  };

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    const { data } = await api.get("/purchase/purchases", { params });
    setPurchases(data);
  };
  const loadSummary = () => api.get("/purchase/dashboard/summary").then(({ data }) => setSummary(data)).catch(() => {});

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/purchase/suppliers", { params: { status: "active" } }).then(({ data }) => setSuppliers(data));
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
    load(); loadSummary();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  // Edit: load the purchase's lines into the same form (item ids preserved so debit notes keep
  // pointing at the right line). Supplier is locked on edit.
  const openEdit = async (p) => {
    const { data } = await api.get(`/purchase/purchases/${p.id}`);
    const toFineStr = (v) => (v === null || v === undefined ? "" : String(v));
    setForm({
      supplier_id: data.supplier_id, purchase_date: data.purchase_date, gst_pct: data.gst_pct ?? 3,
      payment_method: data.payment_method || "bank_transfer", notes: data.notes || "", attachments: data.attachments || [],
      items: (data.items || []).map((it) => {
        const base = emptyItem();
        const finePct = it.purity_fine_pct ?? "";
        return { ...base, ...it, _key: it.id || base._key,
          line_amount: it.purchase_type === "jewellery" || it.purchase_type === "gold" || it.purchase_type === "diamond" || it.purchase_type === "melting" ? "" : toFineStr(it.line_amount),
          purity_fine_pct: toFineStr(finePct),
          purity_preset: finePct === "" || finePct === null ? base.purity_preset : (["995", "999", "916", "750"].includes(String(Math.round(Number(finePct)))) ? String(Math.round(Number(finePct))) : "custom"),
          making_charges: it.making_type === "flat" ? it.making_charges : it.making_input ?? it.making_charges,
        };
      }),
    });
    setEditing(data); setDialogOpen(true);
  };
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const setItem = (i, patch) => setForm({ ...form, items: form.items.map((it, x) => (x === i ? { ...it, ...patch } : it)) });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, x) => x !== i) });

  // Editing ANY one of Gross / Net / Stone recalculates exactly one dependent field so the
  // invariant Gross = Net + Stone always holds — "enter any two, the third is calculated".
  // (Gold / Melting lines only — Jewellery uses the stricter `syncJewelleryWeights` below.)
  const setWeightField = (i, field, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, x) => {
        if (x !== i) return it;
        const next = { ...it, [field]: value };
        const g = Number(next.gross_weight_g) || 0;
        const n = Number(next.net_weight_g) || 0;
        const s = Number(next.stone_weight_g) || 0;
        if (field === "gross_weight_g" || field === "stone_weight_g") next.net_weight_g = round3(Math.max(0, g - s));
        else if (field === "net_weight_g") next.gross_weight_g = round3(n + s);
        return next;
      }),
    }));
  };
  // Jewellery strict weight logic: Stone Wt (g) and Net Wt (g) are READ-ONLY and always derived —
  // Stone = Diamond Wt (ct) x 0.2, Net = Gross − Stone. Recomputes instantly on every Gross Wt or
  // Diamond Wt keystroke; no manual click needed. Mirrors the backend's strict rule exactly.
  const syncJewelleryWeights = (i, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, x) => {
        if (x !== i) return it;
        const next = { ...it, ...patch };
        const w = resolveWeights(next);
        next.gross_weight_g = w.gross_weight_g;
        next.stone_weight_g = w.stone_weight_g;
        next.net_weight_g = w.net_weight_g;
        return next;
      }),
    }));
  };

  const previewSubtotal = form.items.reduce((s, it) => s + itemPreviewTotal(it), 0);
  const previewGst = Math.round(previewSubtotal * (Number(form.gst_pct) || 0) / 100);
  const previewTotal = previewSubtotal + previewGst;

  const save = async () => {
    if (!form.supplier_id) { toast.error("Select a supplier"); return; }
    if (form.items.some((it) => !it.item_name.trim())) { toast.error("Enter an item name for every line"); return; }
    for (const it of form.items) {
      if (["gold", "jewellery", "melting"].includes(it.purchase_type)) {
        const w = resolveWeights(it);
        if ((Number(it.gross_weight_g) || Number(it.stone_weight_g) || Number(it.net_weight_g)) && w.net_weight_g <= 0) {
          toast.error(`"${it.item_name || it.purchase_type}" — Net Weight must be greater than zero (check Gross/Stone/Diamond weight)`);
          return;
        }
      }
    }
    try {
      const payload = { ...form, items: form.items.map((it) => ({ ...it, line_amount: it.line_amount === "" ? null : Number(it.line_amount), purity_fine_pct: it.purity_fine_pct === "" || it.purity_fine_pct === null || it.purity_fine_pct === undefined ? null : Number(it.purity_fine_pct) })) };
      if (editing) {
        const { data } = await api.put(`/purchase/purchases/${editing.id}`, payload);
        const d = data.delta || {};
        toast.success(`Purchase ${editing.purchase_no} updated`, {
          description: `Ledger delta: ${d.gold_fine_g ? `${d.gold_fine_g > 0 ? "+" : ""}${d.gold_fine_g} g fine` : "no gold change"} · ${d.rupee ? `${d.rupee > 0 ? "+" : ""}₹${Math.abs(d.rupee).toLocaleString("en-IN")}` : "no cash change"}${data.bill?.repriced_pieces ? ` · ${data.bill.repriced_pieces} in-stock piece(s) re-priced` : ""}`,
        });
      } else {
        await api.post("/purchase/purchases", payload);
        toast.success("Purchase recorded");
      }
      setDialogOpen(false); setEditing(null); load(); loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save purchase"); }
  };

  const openDetail = async (p) => { const { data } = await api.get(`/purchase/purchases/${p.id}`); setDetail(data); };

  const openPay = (p) => { setDetail(p); setPayForm({ amount: p.outstanding_amount || "", mode: "bank_transfer", cash_mode: "bank_transfer", reference: "", note: "", gold_weight_g: "", gold_purity: "995", gold_purity_fine_pct: "", rate_per_gram_24k: "" }); setPayOpen(true); };
  const recordPayment = async () => {
    try {
      const payload = {
        mode: payForm.mode, reference: payForm.reference, note: payForm.note,
        amount: payForm.amount === "" ? 0 : Number(payForm.amount),
        gold_weight_g: payForm.gold_weight_g === "" ? 0 : Number(payForm.gold_weight_g),
        gold_purity: payForm.gold_purity,
        gold_purity_fine_pct: payForm.gold_purity_fine_pct === "" ? null : Number(payForm.gold_purity_fine_pct),
        rate_per_gram_24k: payForm.rate_per_gram_24k === "" ? null : Number(payForm.rate_per_gram_24k),
      };
      await api.post(`/purchase/purchases/${detail.id}/payments`, payload);
      toast.success(payForm.mode === "gold_to_gold" ? "Metal payment recorded — fine gold stock debited, supplier gold ledger credited" : "Payment recorded"); setPayOpen(false); load(); loadSummary(); setDetail(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record payment"); }
  };

  const openReturn = (p) => { setDetail(p); setReturnSel({}); setPieceSel({}); setReturnReason(""); setReturnOpen(true); };
  const submitReturn = async () => {
    const items = Object.entries(returnSel).filter(([, v]) => v.checked).map(([item_id, v]) => ({ item_id, quantity: Number(v.quantity) || 1, amount: v.amount === "" || v.amount === undefined ? null : Number(v.amount) }));
    // Specific pieces (jewellery): group the ticked barcodes under their purchase line.
    const pieceIds = Object.entries(pieceSel).filter(([, v]) => v).map(([id]) => id);
    if (pieceIds.length) {
      const jewLine = (detail?.items || []).find((it) => it.purchase_type === "jewellery");
      if (jewLine) items.push({ item_id: jewLine.id, quantity: pieceIds.length, piece_ids: pieceIds });
    }
    if (items.length === 0) { toast.error("Select at least one item or piece to return"); return; }
    try {
      const { data } = await api.post(`/purchase/purchases/${detail.id}/returns`, { items, reason: returnReason });
      toast.success(`Debit Note ${data.debit_note?.debit_note_no || ""} created`, { description: `${formatINR(data.debit_note?.total)} deducted from ${detail.supplier_name}'s ledger${data.debit_note?.gold_fine_g ? ` · ${data.debit_note.gold_fine_g} g fine gold` : ""}` });
      setReturnOpen(false); load(); loadSummary(); setDetail(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record return"); }
  };

  const doCancel = async () => {
    try { await api.post(`/purchase/purchases/${cancelFor.id}/cancel`); toast.success("Purchase cancelled"); setCancelFor(null); load(); loadSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || "Cancel failed"); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    try { await api.delete(`/purchase/purchases/${deleteFor.id}`); toast.success("Purchase deleted"); setDeleteFor(null); load(); loadSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete purchase"); }
  };

  const exportCsv = () => downloadBlob("/purchase/reports/register.csv", "Purchase-Register.csv").catch(() => toast.error("Export failed"));

  const uploadAttachmentFile = async (file) => {
    if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} is too large (max 8MB)`); return null; }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/purchase/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { url: data.url, filename: data.filename || file.name };
  };

  const handleCreateAttachmentUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      const uploaded = [];
      for (const file of files) {
        try { const res = await uploadAttachmentFile(file); if (res) uploaded.push(res); }
        catch (err) { toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`); }
      }
      if (uploaded.length) { setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] })); toast.success(`${uploaded.length} file(s) attached`); }
    } finally { setUploadingAttachment(false); }
  };
  const removeCreateAttachment = (idx) => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));

  const handleDetailAttachmentUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !detail) return;
    setUploadingAttachment(true);
    try {
      let lastPurchase = null;
      for (const file of files) {
        try {
          const uploaded = await uploadAttachmentFile(file);
          if (!uploaded) continue;
          const { data } = await api.post(`/purchase/purchases/${detail.id}/attachments`, uploaded);
          lastPurchase = data.purchase;
        } catch (err) { toast.error(err.response?.data?.detail || `Failed to attach ${file.name}`); }
      }
      if (lastPurchase) { setDetail(lastPurchase); toast.success("Attachment added"); load(); }
    } finally { setUploadingAttachment(false); }
  };

  return (
    <AdminShell title="Purchases">
      <PurchasesSummaryCards summary={summary} onOpenLedger={openCompanyLedger} />

      <PurchasesFiltersBar filters={filters} setFilters={setFilters} suppliers={suppliers} onExport={exportCsv} onNewPurchase={openCreate} />

      <PurchasesTable purchases={purchases} staff={staff} onOpenDetail={openDetail} onPay={openPay} onReturn={openReturn} onEdit={openEdit} onCancel={setCancelFor} onDelete={setDeleteFor} />

      <NewPurchaseDialog
        open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} form={form} setForm={setForm} editing={editing}
        suppliers={suppliers} products={products} onAddItem={addItem} onSetItem={setItem}
        onSetWeightField={setWeightField} onSyncJewelleryWeights={syncJewelleryWeights} onRemoveItem={removeItem}
        previewSubtotal={previewSubtotal} previewGst={previewGst} previewTotal={previewTotal}
        uploadingAttachment={uploadingAttachment} createFileInputRef={createFileInputRef}
        onUploadAttachment={handleCreateAttachmentUpload} onRemoveAttachment={removeCreateAttachment} onSave={save}
      />

      <PurchaseDetailDialog
        detail={!payOpen && !returnOpen ? detail : null} onClose={() => setDetail(null)}
        uploadingAttachment={uploadingAttachment} detailFileInputRef={detailFileInputRef}
        onUploadAttachment={handleDetailAttachmentUpload}
      />

      <RecordPurchasePaymentDialog open={payOpen} onClose={() => { setPayOpen(false); setDetail(null); }} detail={detail} payForm={payForm} setPayForm={setPayForm} onSubmit={recordPayment} />

      <PurchaseReturnDialog open={returnOpen} onClose={() => { setReturnOpen(false); setDetail(null); }} detail={detail} returnSel={returnSel} setReturnSel={setReturnSel} pieceSel={pieceSel} setPieceSel={setPieceSel} returnReason={returnReason} setReturnReason={setReturnReason} onSubmit={submitReturn} />

      <CancelPurchaseDialog cancelFor={cancelFor} onClose={() => setCancelFor(null)} onConfirm={doCancel} />

      <DeletePurchaseDialog deleteFor={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={doDelete} />

      <CompanyLedgerDialog open={ledgerOpen} onClose={() => setLedgerOpen(false)} companyLedger={companyLedger} />
    </AdminShell>
  );
}
